// Data-driven definition of the 9-section assessment.
// Each section: { id, title, subtitle, group, blocks: [...] }
// Block types:
//   info      { type, heading, paragraphs:[], bullets:[], variant }
//   field     { type:'field', key, label, input, options?, required?, note?, placeholder?, showIf? }
//     input: text | email | tel | textarea | select | radio | checkboxes | file
//   ack       { type:'ack', key, label }

const DEGREE_OPTIONS = [
  "High School/GED", "Associate Degree", "Bachelor's Degree", "Master's Degree",
  "Doctoral Degree", "Registered Nurse", "Other",
];
const YN = ["Yes", "No"];
const YNU = ["Yes", "No", "Unsure"];
const CERT = ["Yes", "No", "Previously certified"];

const REFERRAL_SOURCES = [
  "A current GIC&E employee or contractor",
  "A friend, colleague, or family member",
  "GIC&E website (giceloves.org)",
  "Social media",
  "Job board (Indeed, LinkedIn, etc.)",
  "Community organization or event",
  "Other",
];

const EXPERIENCE_AREAS = [
  "Mental health", "Behavioral health", "Case management", "Psychosocial rehabilitation",
  "Skills training", "Crisis services", "Social-emotional learning", "Special education",
  "Behavior intervention", "Community-based services", "Care coordination",
  "Resource coordination/linkage", "Youth services", "Family support",
  "Foster care/CPS populations", "Substance-use services",
  "Intellectual/developmental disabilities", "School-based services", "Other",
];

const DOC_TYPES = [
  "Progress notes", "Case management notes", "Psychosocial rehabilitation notes",
  "Skills training notes", "Crisis documentation", "Treatment/service plans", "Assessments",
  "Care coordination documentation", "Medicaid documentation",
  "Insurance-funded service documentation", "Electronic health record documentation",
  "None of the above",
];

export const SECTIONS = [
  {
    id: "education",
    title: "Education & Qualification",
    subtitle: "Section 1 of 9",
    group: "qualifications",
    blocks: [
      { type: "field", key: "legal_name", label: "Legal Name", input: "text", required: true },
      { type: "field", key: "preferred_name", label: "Preferred Name", input: "text" },
      { type: "field", key: "email", label: "Email Address", input: "email", required: true },
      { type: "field", key: "phone", label: "Phone Number", input: "tel", required: true },
      { type: "field", key: "city_state", label: "City and State", input: "text", required: true },
      { type: "field", key: "referral_source", label: "How did you hear about this opportunity / Who referred you to apply?", input: "select", options: REFERRAL_SOURCES, required: true },
      { type: "field", key: "referral_name", label: "Name of the person who referred you", input: "text",
        note: "If a current employee, contractor, or someone you know referred you, please enter their full name. Enter “NA” if you were not referred by a specific person." },
      { type: "field", key: "highest_degree", label: "Highest completed degree", input: "select", options: DEGREE_OPTIONS, required: true },
      { type: "field", key: "college_name", label: "Name of college/university", input: "text" },
      { type: "field", key: "degree_earned", label: "Degree earned", input: "text" },
      { type: "field", key: "major", label: "Major / field of study", input: "text" },
      { type: "field", key: "graduation_year", label: "Graduation year", input: "text" },
      { type: "field", key: "accredited", label: "Is the institution accredited?", input: "radio", options: YNU },
      { type: "field", key: "diploma", label: "Upload diploma", input: "file", required: true },
      { type: "field", key: "transcript", label: "Upload unofficial or official transcript", input: "file", required: true,
        note: "Your transcript may be reviewed to determine whether your completed coursework satisfies applicable qualification requirements." },
      { type: "field", key: "has_license", label: "Do you currently hold a professional license, certification, or credential?", input: "radio", options: YN },
      { type: "field", key: "license_type", label: "Credential / license type", input: "text", showIf: { key: "has_license", eq: "Yes" } },
      { type: "field", key: "license_state", label: "State", input: "text", showIf: { key: "has_license", eq: "Yes" } },
      { type: "field", key: "license_number", label: "License / credential number", input: "text", showIf: { key: "has_license", eq: "Yes" } },
      { type: "field", key: "license_expiration", label: "Expiration date", input: "text", placeholder: "MM/YYYY", showIf: { key: "has_license", eq: "Yes" } },
      { type: "field", key: "prev_qmhpcs", label: "Have you previously worked or been credentialed as a QMHP-CS?", input: "radio", options: YNU },
      { type: "field", key: "prev_medicaid_mhr", label: "Have you previously provided Texas Medicaid Mental Health Rehabilitative Services?", input: "radio", options: YNU },
      { type: "field", key: "prev_mh_tcm", label: "Have you previously provided Mental Health Targeted Case Management?", input: "radio", options: YNU },
      { type: "field", key: "cans_cert", label: "Do you currently hold CANS certification?", input: "radio", options: CERT },
      { type: "field", key: "ansa_cert", label: "Do you currently hold ANSA certification?", input: "radio", options: CERT },
      { type: "field", key: "cans_ansa_file", label: "Upload any applicable CANS/ANSA certificate", input: "file",
        requiredIf: { anyOf: [{ key: "cans_cert", eq: "Yes" }, { key: "ansa_cert", eq: "Yes" }] },
        note: "If you answered “Yes” to holding CANS or ANSA certification, please upload your certificate as proof." },
    ],
  },
  {
    id: "experience",
    title: "Professional Experience",
    subtitle: "Section 2 of 9",
    group: "experience",
    blocks: [
      { type: "field", key: "years_experience", label: "How many years of professional experience do you have working with individuals experiencing mental, behavioral, emotional, developmental, educational, or social needs?", input: "radio",
        options: ["None", "Less than 1 year", "1–2 years", "3–5 years", "5–10 years", "More than 10 years"] },
      { type: "field", key: "experience_areas", label: "Select all applicable experience", input: "checkboxes", options: EXPERIENCE_AREAS },
      { type: "field", key: "experience_description", label: "Briefly describe the experience you believe best prepares you for this work.", input: "textarea", required: true },
      { type: "field", key: "worked_independently", label: "Have you previously worked independently with clients in homes, schools, community settings, or through telehealth?", input: "radio", options: YN },
      { type: "field", key: "independent_description", label: "Describe that experience.", input: "textarea", showIf: { key: "worked_independently", eq: "Yes" } },
    ],
  },
  {
    id: "documentation",
    title: "Documentation Experience",
    subtitle: "Section 3 of 9",
    group: "experience",
    blocks: [
      { type: "field", key: "documentation_types", label: "Have you professionally completed any of the following documentation? Select all.", input: "checkboxes", options: DOC_TYPES },
      { type: "field", key: "ehr_systems", label: "Which electronic health record systems have you used?", input: "text" },
      { type: "field", key: "documentation_timing", label: "How soon after providing a service do you believe documentation should normally be completed?", input: "textarea" },
      { type: "field", key: "documentation_importance", label: "Why is documentation important in a Medicaid-funded mental and behavioral health program?", input: "textarea" },
      { type: "field", key: "talk_vs_skill", label: "What is the difference between documenting what you TALKED ABOUT with a client and documenting a SKILL or INTERVENTION you actually provided?", input: "textarea" },
    ],
  },
  {
    id: "judgment",
    title: "Professional Judgment",
    subtitle: "Section 4 of 9",
    group: "judgment",
    blocks: [
      { type: "info", variant: "scenario", heading: "Scenario", paragraphs: [
        'A parent tells you: "We don\'t have groceries, my electricity is about to be disconnected, my child hasn\'t been attending school, and she has been crying every day."',
      ] },
      { type: "field", key: "judgment_concerns", label: "1. What concerns do you identify?", input: "textarea" },
      { type: "field", key: "judgment_first", label: "2. What would you address first and why?", input: "textarea" },
      { type: "field", key: "judgment_actions", label: "3. What actions would you take?", input: "textarea" },
      { type: "field", key: "judgment_care_coordination", label: "4. Which needs may require care coordination / resource linkage?", input: "textarea" },
      { type: "field", key: "judgment_communicate_team", label: "5. Is there anything you would immediately communicate to another member of the treatment team? Explain.", input: "textarea" },
      { type: "field", key: "judgment_out_of_scope", label: "6. What would you NOT attempt to handle independently because it may fall outside your role or scope?", input: "textarea" },
    ],
  },
  {
    id: "billing_ack",
    title: "Contractor & Billing Acknowledgment",
    subtitle: "Section 5 of 9",
    group: "acknowledgments",
    blocks: [
      { type: "info", paragraphs: [
        "GIC&E provides services in environments in which documentation, authorization, medical necessity, treatment-plan alignment, payer requirements and service requirements matter.",
        "Contractors are responsible for understanding that time spent with a client does not automatically make an activity billable.",
        "Compensation and payment requirements are governed by the executed contractor agreement and applicable company and payer requirements.",
        "Services and documentation that do not satisfy applicable requirements may be determined non-billable or non-reimbursable.",
      ] },
      { type: "ack", key: "ack_bill_1", label: "I understand that providing time to a client does not automatically make an activity billable." },
      { type: "ack", key: "ack_bill_2", label: "I understand that services must be supported by appropriate documentation." },
      { type: "ack", key: "ack_bill_3", label: "I understand that services must remain within my assigned role and scope." },
      { type: "ack", key: "ack_bill_4", label: "I understand that documentation must accurately reflect the service actually performed." },
      { type: "ack", key: "ack_bill_5", label: "I understand that I may never document or bill for a service I did not provide." },
      { type: "ack", key: "ack_bill_6", label: "I understand that duplicate, unsupported, falsified, altered, or otherwise noncompliant documentation may result in nonpayment and/or termination of the contractor relationship." },
      { type: "ack", key: "ack_bill_7", label: "I understand that compensation is governed by my executed contractor agreement." },
    ],
  },
  {
    id: "case_study",
    title: "Competency Case Study",
    subtitle: "Section 6 of 9",
    group: "case_study",
    readonly: true,
    blocks: [
      { type: "info", variant: "banner", heading: "Applied Competency Assessment", paragraphs: [
        "The following case study is designed to evaluate how you translate a treatment plan into an actual client encounter.",
        "You are not expected to know GIC&E's internal documentation format yet.",
        "We are evaluating your ability to think critically, remain within scope, provide a meaningful intervention, recognize care-coordination needs, and document what actually occurred.",
      ] },
      { type: "info", heading: "Case — Maya R., Age 13", paragraphs: [
        "Maya's mother contacts GIC&E and reports that Maya is currently home during the school day.",
        "Maya was recently withdrawn from school after repeated behavioral incidents and conflict with peers.",
        "Mother reports that Maya spends much of the day isolated in her room or watching younger siblings.",
        'Maya has become increasingly frustrated, has difficulty managing anger, and frequently states: "Nobody listens to me."',
        "Mother reports difficulty establishing a daily routine and is unsure what educational options are currently available.",
        "Maya has already been assessed by the LPC and determined appropriate for services.",
      ] },
      { type: "info", variant: "plan", heading: "Treatment Plan", goals: [
        { title: "Goal 1 — Emotional Regulation",
          body: "Maya will improve her ability to recognize and appropriately respond to strong emotions.",
          objective: "Maya will identify at least three personal triggers and demonstrate at least three coping/emotional-regulation strategies.",
          interventions: ["Teach identification of emotional triggers", "Teach and practice coping strategies", "Use real-life scenarios to practice appropriate responses"] },
        { title: "Goal 2 — Daily Functioning & Routine",
          body: "Maya will improve consistency in completing developmentally appropriate daily activities.",
          objective: "Maya will develop and follow a structured weekday routine containing identified responsibilities and self-care activities.",
          interventions: ["Teach time-management and organizational skills", "Assist Maya in developing a visual daily schedule", "Practice problem-solving around barriers"] },
        { title: "Goal 3 — Community & Educational Stability",
          body: "Maya and her caregiver will improve access to appropriate educational and community resources.",
          objective: "Family will identify and connect with appropriate educational/community supports.",
          interventions: ["Assess barriers to school participation", "Coordinate/link family with appropriate educational/community resources", "Follow up regarding access and barriers"] },
      ] },
    ],
  },
  {
    id: "doc_training",
    title: "Documentation Training",
    subtitle: "Section 7 of 9",
    group: "case_study",
    readonly: true,
    blocks: [
      { type: "info", variant: "banner", heading: "A strong service note should help the reader understand:", paragraphs: [] },
      { type: "info", heading: "Why?", paragraphs: ["Why was today's service necessary? Connect the encounter to the client's assessed need and treatment-plan objective."] },
      { type: "info", heading: "What?", paragraphs: ["What did you actually do? Describe observable interventions."],
        bullets: ['Avoid vague documentation such as: "Talked with client." / "Discussed coping." / "Worked on goals."',
                  "Instead, explain what was taught, demonstrated, modeled, practiced, developed, coordinated, assessed, or reinforced."] },
      { type: "info", heading: "How did the client respond?", paragraphs: ["What did the client say, demonstrate, practice, identify, complete, understand, resist, or struggle with?"] },
      { type: "info", heading: "What was accomplished?", paragraphs: ["What progress, barrier, need, or response became evident?"] },
      { type: "info", heading: "What happens next?", paragraphs: ["Identify an appropriate next step related to the treatment plan."] },
      { type: "info", variant: "warning", heading: "Important", paragraphs: [
        "Do not simply copy the treatment plan into your note.",
        "Your documentation should demonstrate what actually occurred during the encounter.",
      ] },
    ],
  },
  {
    id: "assignment",
    title: "Applicant Assignment",
    subtitle: "Section 8 of 9",
    group: "case_study",
    blocks: [
      { type: "info", variant: "banner", paragraphs: ["You have been assigned Maya for a 60–90-minute service. Answer the following:"] },
      { type: "field", key: "assign_goal", label: "1. Which treatment-plan goal/objective would you address during this visit?", input: "textarea" },
      { type: "field", key: "assign_why", label: "2. Why did you select this goal/objective?", input: "textarea" },
      { type: "field", key: "assign_service_type", label: "3. What type of service do you believe you would be providing?", input: "textarea" },
      { type: "field", key: "assign_walkthrough", label: "4. Walk us through the 60–90-minute encounter. What would you actually do?", input: "textarea" },
      { type: "field", key: "assign_skill", label: "5. Identify at least one specific skill you would teach Maya.", input: "textarea" },
      { type: "field", key: "assign_practice", label: "6. How would Maya actively practice the skill rather than simply discuss it?", input: "textarea" },
      { type: "field", key: "assign_refuse", label: "7. What would you do if Maya refused to participate?", input: "textarea" },
      { type: "field", key: "assign_no_school", label: "8. During the visit, Maya's mother tells you Maya currently has no school placement. What would you do with that information?", input: "textarea" },
      { type: "field", key: "assign_team_info", label: "9. What information, if any, would you communicate to the treatment team?", input: "textarea" },
      { type: "field", key: "assign_out_of_scope", label: "10. Identify something you would NOT do because it falls outside your role or requires another qualified professional.", input: "textarea" },
      { type: "info", variant: "warning", heading: "Documentation Challenge", paragraphs: [
        "Now assume the service you described actually occurred. Write the progress note you would submit documenting this 60–90-minute encounter.",
      ] },
      { type: "field", key: "progress_note", label: "Progress note", input: "textarea", large: true },
      { type: "field", key: "billable_explanation", label: "Based on the service and documentation you created, explain why you believe this encounter would be billable.", input: "textarea" },
      { type: "field", key: "verify_before_billing", label: "What additional information would you want to verify before submitting this service for billing?", input: "textarea" },
    ],
  },
  {
    id: "certification",
    title: "Applicant Certification",
    subtitle: "Section 9 of 9",
    group: "certification",
    blocks: [
      { type: "ack", key: "cert_1", label: "I certify that the information provided in this assessment is accurate to the best of my knowledge." },
      { type: "ack", key: "cert_2", label: "I certify that the written responses submitted are my responses and accurately represent my current knowledge and abilities." },
      { type: "ack", key: "cert_3", label: "I understand that GIC&E may verify education, credentials, employment history and professional qualifications." },
      { type: "ack", key: "cert_4", label: "I understand that completing this assessment does not guarantee an interview, contract, caseload, hours, compensation, or placement." },
    ],
  },
];

// Fields grouped for the Admin detail tabs
export const ADMIN_TAB_SECTIONS = {
  QUALIFICATIONS: ["education"],
  EXPERIENCE: ["experience", "documentation"],
  JUDGMENT: ["judgment"],
  "CASE STUDY": ["assignment"],
};

export function fieldLabel(key) {
  for (const s of SECTIONS) {
    for (const b of s.blocks) {
      if ((b.type === "field" || b.type === "ack") && b.key === key) return b.label;
    }
  }
  return key;
}

export const FILE_KEYS = ["diploma", "transcript", "cans_ansa_file"];
